import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { isApiError } from "@profitlens/api-client";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";

import { LanguageSwitcher } from "@/components/language-switcher";
import { translateError } from "@/lib/api-error";
import { login } from "@/lib/session";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => login({ email, password }),
    onSuccess: () => {
      const from =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    },
    onError: (err: unknown) => {
      setError(translateError(err, t));
    },
  });

  return (
    <div className="relative grid min-h-dvh place-items-center bg-background px-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-sm p-6 shadow-card">
        <div className="mb-1 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
            PL
          </div>
          <h1 className="text-base font-semibold">{t("app.name")}</h1>
        </div>
        <p className="mb-6 flex items-center gap-1.5 text-xs text-muted">
          <ShieldCheck className="h-3.5 w-3.5 text-brand" />
          {t("login.subtitle")}
        </p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error ? (
            <p className="text-sm text-danger">
              {error}
              {isApiError(mutation.error) && mutation.error.status === 401
                ? `（${t("login.notAdmin")}）`
                : null}
            </p>
          ) : null}
          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending || !email || !password}
          >
            {mutation.isPending ? t("login.submitting") : t("login.submit")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
